import { useState } from 'react';

interface Props {
  onClose: () => void;
}

export default function UploadProductModal({ onClose }: Props) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: 'stationery',
    price: '',
    originalPrice: '',
    stock: '',
    weight: '',
    description: '',
  });

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <div>
            <h2 className="font-black text-gray-900">上传新商品</h2>
            <p className="text-xs text-gray-400 mt-0.5">步骤 {step}/2</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-green-600 text-3xl"></i>
            </div>
            <h3 className="font-black text-gray-900 text-lg mb-2">商品已提交审核</h3>
            <p className="text-gray-400 text-sm">平台将在1-3个工作日内完成审核，审核通过后商品将自动上架销售。</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
              <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
            </div>

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">商品名称 *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => handleChange('name', e.target.value)}
                    placeholder="请输入商品名称，建议包含品牌和规格"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">商品类目 *</label>
                  <select
                    value={form.category}
                    onChange={e => handleChange('category', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="stationery">文具/钢笔</option>
                    <option value="books">教辅书籍</option>
                    <option value="clothing">学生服装</option>
                    <option value="digital">学习设备</option>
                    <option value="desk">学习桌椅</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">售价（元）*</label>
                    <input
                      type="number"
                      value={form.price}
                      onChange={e => handleChange('price', e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">原价（元）</label>
                    <input
                      type="number"
                      value={form.originalPrice}
                      onChange={e => handleChange('originalPrice', e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">库存数量 *</label>
                    <input
                      type="number"
                      value={form.stock}
                      onChange={e => handleChange('stock', e.target.value)}
                      placeholder="件"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">重量（kg）*</label>
                    <input
                      type="number"
                      value={form.weight}
                      onChange={e => handleChange('weight', e.target.value)}
                      placeholder="用于计算运费"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">商品描述</label>
                  <textarea
                    value={form.description}
                    onChange={e => handleChange('description', e.target.value)}
                    placeholder="请详细描述商品特点、材质、适用人群等..."
                    rows={3}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{form.description.length}/500</p>
                </div>
                <button
                  onClick={() => setStep(2)}
                  disabled={!form.name || !form.price || !form.stock || !form.weight}
                  className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  下一步：上传图片
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-5">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-2 block">商品主图 *</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-red-300 transition-colors cursor-pointer">
                    <i className="ri-image-add-line text-4xl text-gray-300 mb-2 block"></i>
                    <p className="text-sm text-gray-400">点击上传商品主图</p>
                    <p className="text-xs text-gray-300 mt-1">支持 JPG、PNG，建议尺寸 800×800px</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-2 block">商品详情图（最多5张）</label>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="aspect-square border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer hover:border-red-300 transition-colors">
                        <i className="ri-add-line text-gray-300 text-xl"></i>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                    商品图片将经过平台审核，请确保图片清晰、真实，不含违规内容。
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3.5 border border-gray-200 text-gray-600 font-semibold rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    上一步
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    提交审核
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
